import { useMutation, UseMutationOptions, useQueryClient } from 'react-query'
import { AIJobStatus, BrandKit } from 'types'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { fetchBrandKit } from 'hooks/queries/brandKits/useBrandKit'
import { deleteBrandKit } from './brandKits/useDeleteBrandKit'

interface Variables {
  signal?: AbortSignal
}

export interface UseBrandKitOptions extends Omit<UseMutationOptions<BrandKit, Error, Variables>, 'mutationFn'> {}

interface Options extends UseBrandKitOptions {
  /**
   * Callback to be called on each poll interval. Useful for updating the UI
   * with the latest data.
   */
  onPollInterval?: (brandKit: BrandKit) => void
}

export const useFetchBrandKitSync = (
  {
    brandKitId,
    includeAllMediaFiles,
    waitForBadImageDetection,
  }: { brandKitId: number; includeAllMediaFiles?: boolean; waitForBadImageDetection?: boolean },
  options?: Options,
) => {
  const workspaceId = useCurrentWorkspaceId()
  const { onPollInterval, ...restOptions } = options ?? {}
  const queryClient = useQueryClient()

  return useMutation<BrandKit, Error, Variables>(async (vars) => {
    const { signal } = vars

    let brandKit: BrandKit | undefined

    let interval = 500
    do {
      const result = await fetchBrandKit({ id: brandKitId, workspaceId, includeAllMediaFiles })

      brandKit = result.brand_kit

      onPollInterval?.(brandKit)

      if (signal?.aborted && brandKit) {
        await deleteBrandKit(brandKit.id, workspaceId)
        throw new DOMException('The operation was aborted.', 'AbortError')
      }

      if (brandKit?.status === AIJobStatus.Failed) {
        throw new Error('Failed to populate brand kit')
      }

      await new Promise((resolve) => {
        setTimeout(resolve, interval)
      })
    } while (
      ![AIJobStatus.Failed, AIJobStatus.Expired, AIJobStatus.Successful].includes(brandKit?.status) ||
      (waitForBadImageDetection && !brandKit?.bad_image_detection_completed)
    )

    queryClient.invalidateQueries(['workspaces', workspaceId, 'brandKits'])

    return brandKit
  }, restOptions)
}
